import React, { useState } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Système unifié des statuts opérationnels (Refonte UX 07/05/2026)
 *
 * Source unique de vérité pour les couleurs/labels des statuts dans toute l'app.
 * Couplé à un Drawer de légende accessible depuis n'importe où.
 */

export const STATUS_DEFINITIONS = {
  // Étapes initiales
  nouvelle:               { label: 'Nouvelle',                color: 'slate',   description: 'Demande créée, pas encore traitée.' },
  demande_enregistree:    { label: 'Enregistrée',             color: 'slate',   description: 'Demande validée par notre équipe.' },
  en_attente_de_devis:    { label: 'En attente de devis',     color: 'amber',   description: 'Notre équipe prépare votre devis.' },
  devis_emis:             { label: 'Devis émis',              color: 'blue',    description: 'Le devis vous a été transmis.' },
  pending_validation:     { label: 'À valider',               color: 'amber',   description: 'Devis en attente de votre validation.' },
  acceptee:               { label: 'Acceptée',                color: 'emerald', description: 'Devis accepté, opération planifiée.' },

  // Phases opérationnelles
  planifiee:              { label: 'Planifiée',               color: 'blue',    description: 'Chauffeur et véhicule assignés.' },
  en_cours:               { label: 'En cours',                color: 'amber',   description: 'Chargement ou trajet en cours.' },
  en_chargement:          { label: 'Chargement',              color: 'amber',   description: "Chargement de la marchandise sur le véhicule." },
  en_route:               { label: 'En route',                color: 'amber',   description: 'En cours de transport.' },
  en_dechargement:        { label: 'Déchargement',            color: 'amber',   description: "Déchargement à destination." },
  livree:                 { label: 'Livrée',                  color: 'emerald', description: 'Marchandise livrée à destination.' },
  delivered:              { label: 'Livrée',                  color: 'emerald', description: 'Marchandise livrée à destination.' },

  // Phases financières
  en_attente_paiement:    { label: 'En attente de paiement',  color: 'amber',   description: 'Facture émise, paiement attendu.' },
  pending_payment:        { label: 'En attente de paiement',  color: 'amber',   description: 'Facture émise, paiement attendu.' },
  partially_paid:         { label: 'Partiellement payée',     color: 'amber',   description: 'Acompte reçu, solde en attente.' },
  payee:                  { label: 'Payée',                   color: 'emerald', description: 'Paiement reçu intégralement.' },
  paid:                   { label: 'Payée',                   color: 'emerald', description: 'Paiement reçu intégralement.' },
  facturee:               { label: 'Facturée',                color: 'blue',    description: 'Facture émise.' },
  invoiced:               { label: 'Facturée',                color: 'blue',    description: 'Facture émise.' },
  terminee:               { label: 'Terminée',                color: 'emerald', description: 'Opération clôturée.' },
  completed:              { label: 'Terminée',                color: 'emerald', description: 'Opération clôturée.' },

  // États d'incident
  en_retard:              { label: 'En retard',               color: 'red',     description: 'Délai de livraison dépassé.' },
  cancelled:              { label: 'Annulée',                 color: 'slate',   description: 'Opération annulée.' },
  annulee:                { label: 'Annulée',                 color: 'slate',   description: 'Opération annulée.' },
  refused:                { label: 'Refusée',                 color: 'red',     description: 'Devis refusé par le client.' },
  refusee:                { label: 'Refusée',                 color: 'red',     description: 'Devis refusé par le client.' },

  // POD client
  validated:              { label: 'Validée par client',      color: 'emerald', description: 'Le client a confirmé la bonne réception.' },
  disputed:               { label: 'Litige',                  color: 'red',     description: 'Le client a signalé un litige.' },
};

const COLOR_CLASSES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-50 text-amber-700 border-amber-200',
  blue:    'bg-blue-50 text-blue-700 border-blue-200',
  red:     'bg-red-50 text-red-700 border-red-200',
  slate:   'bg-slate-100 text-slate-700 border-slate-200',
};

/** Petit badge réutilisable */
export const StatusBadge = ({ status, className }) => {
  const def = STATUS_DEFINITIONS[(status || '').toLowerCase()] || {
    label: status || '—',
    color: 'slate',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium',
        COLOR_CLASSES[def.color] || COLOR_CLASSES.slate,
        className,
      )}
      data-testid={`status-${status}`}
    >
      <span className={cn(
        'w-1.5 h-1.5 rounded-full',
        def.color === 'emerald' && 'bg-emerald-500',
        def.color === 'amber' && 'bg-amber-500',
        def.color === 'blue' && 'bg-blue-500',
        def.color === 'red' && 'bg-red-500',
        def.color === 'slate' && 'bg-slate-400',
      )} />
      {def.label}
    </span>
  );
};

/** Bouton ? qui ouvre un drawer avec la légende complète des statuts */
export const StatusLegendDrawer = ({ triggerLabel = 'Légende' }) => {
  const [open, setOpen] = useState(false);
  // Regrouper par couleur pour lisibilité
  const grouped = {
    emerald: [], amber: [], blue: [], red: [], slate: [],
  };
  const seen = new Set();
  Object.entries(STATUS_DEFINITIONS).forEach(([key, def]) => {
    const sig = `${def.label}|${def.color}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    grouped[def.color]?.push({ key, ...def });
  });

  const COLOR_LABELS = {
    emerald: 'Succès / Validé',
    amber:   'En cours / Attention',
    blue:    'Information',
    red:     'Erreur / Retard',
    slate:   'Neutre / Annulé',
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-slate-900 gap-1"
          data-testid="status-legend-trigger">
          <Info className="w-3.5 h-3.5" />
          {triggerLabel}
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto" data-testid="status-legend-drawer">
        <SheetHeader>
          <SheetTitle>Légende des statuts</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          {Object.entries(grouped).map(([color, items]) => (
            items.length > 0 && (
              <div key={color}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {COLOR_LABELS[color]}
                </h4>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.key} className="flex items-start gap-3">
                      <StatusBadge status={item.key} />
                      <p className="text-xs text-slate-600 flex-1">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default StatusBadge;
